<template>
    <div class="notification-wrapper">
        <!-- 消息面板 -->
        <div class="notif-panel" :class="{ open: isOpen }">
            <div class="panel-topline"></div>

            <div class="panel-header">
                <span class="panel-title">{{ $t('system.notify.panelTitle') }}</span>
            </div>

            <div class="panel-tabs">
                <button v-for="(tab, index) in tabs" :key="index" class="tab-btn"
                    :class="{ active: currentTab === index }" @click="handleTabClick(index)">
                    {{ tab.label }}
                    <span v-if="tab.count > 0" class="tab-count">{{ tab.count }}</span>
                </button>
            </div>

            <div class="msg-list" @scroll="handleMessageScroll">
                <div v-for="msg in filteredMessages" :key="msg.id" class="msg-card" :class="{
                    unread: msg.unread,
                    warn: msg.type === 'warn',
                    success: msg.type === 'success'
                }" @click="handleMsgClick(msg)">
                    <div class="msg-icon" :class="msg.iconColor">
                        {{ msg.icon }}
                    </div>
                    <div class="msg-body">
                        <div class="msg-title">
                            <span v-if="msg.unread" class="unread-dot"></span>
                            <span class="msg-title-text">{{ msg.title }}</span>
                            <span v-if="msg.isNew" class="msg-new-badge">NEW</span>
                            <span v-if="msg.tag" class="msg-tag" :class="msg.tagClass">{{ msg.tag }}</span>
                        </div>
                        <div class="msg-desc">{{ msg.desc }}</div>
                        <div class="msg-time">{{ msg.time }}</div>
                    </div>
                </div>
                <div v-if="loadingMore" class="msg-loading-more">{{ $t('system.notify.loading') }}</div>
                <div v-if="!loadingList && !filteredMessages.length" class="msg-empty">{{ $t('system.notify.empty') }}</div>
            </div>
        </div>

        <!-- 详情抽屉 (Teleport 到 body 防止父元素 overflow 截断) -->
        <Teleport to="body">
            <div class="drawer-overlay" :class="{ open: drawerOpen }" @click="closeDrawer"></div>

            <div class="drawer" :class="{ open: drawerOpen }">
                <div class="drawer-topline"></div>
                <div class="drawer-header">
                    <span class="drawer-title">{{ currentDrawer.title }}</span>
                    <button class="drawer-close" @click="closeDrawer">✕</button>
                </div>

                <div class="drawer-body markdown-body">
                    <div v-if="drawerLoading" class="drawer-loading">
                        <span class="loading-spinner"></span>
                        <span>{{ $t('system.notify.loading') }}</span>
                    </div>
                    <div v-else v-html="parsedDrawerContent"></div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { listNotice } from '@/api/system/notice';
import emitter from '@/utils/eventBus';
import {
    EVENT_GLOBAL_NOTICE_RECEIVED,
    applyNoticeNewRecords,
    createNoticeNewRecord,
    isActiveNotice,
    isDuplicateNoticePush,
    mergeNoticeMessage,
    normalizeNoticePayload,
    parseNoticeNewRecords,
    removeNoticeNewRecord,
    stringifyNoticeNewRecords,
    toNotifyMessage
} from './notice-adapter';

const NOTIFY_NEW_STORAGE_KEY = 'notify_new_records';
const NOTIFY_PUSH_RECORD_STORAGE_KEY = 'notify_push_records';
const NOTIFY_PAGE_SIZE = 10;
const NOTIFY_SCROLL_DEBOUNCE = 180;
const NOTICE_TYPE_BY_CATEGORY = {
    all: '',
    notice: '1',
    announcement: '2',
    update: '3'
};

const { t } = useI18n();

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
})
const emits = defineEmits(['close', 'unread-change'])

// --- 状态数据 ---
const isOpen = computed(() => props.show);
const currentTab = ref(0);

const drawerOpen = ref(false);
const drawerLoading = ref(false);
const drawerContent = ref('');
const currentDrawer = ref({ title: '' });

const tabs = ref([
    { category: 'all', label: t('system.notify.tabAll'), count: 0 },
    { category: 'notice', label: t('system.notify.tabNotice'), count: 0 },
    { category: 'announcement', label: t('system.notify.tabAnnouncement'), count: 0 },
    { category: 'update', label: t('system.notify.tabUpdate'), count: 0 },
]);

const messages = ref([]);
const loadingList = ref(false);
const loadingMore = ref(false);
const total = ref(0);
const queryPage = ref(1);
let scrollTimer = null;

// --- 计算属性 ---
const parsedDrawerContent = computed(() => {
    return drawerContent.value || '';
});

const currentNoticeType = computed(() => {
    return NOTICE_TYPE_BY_CATEGORY[tabs.value[currentTab.value]?.category] || '';
});

const filteredMessages = computed(() => {
    return messages.value;
});

// --- 方法 ---
const closePanelGlobally = (e) => {
    if (isOpen.value && !e.target.closest('.notification-wrapper')) {
        emits('close')
    }
};

/**
 * 读取本地持久化的通知状态记录。
 * NEW 状态和已处理 socket 指纹都复用同一结构，读取时统一做异常兜底，避免 localStorage 损坏影响面板渲染。
 *
 * @param key localStorage 中的记录键名
 * @returns 去重、校验后的通知状态记录
 */
const getNoticeRecords = (key) => {
    return parseNoticeNewRecords(localStorage.getItem(key));
};

/**
 * 保存通知状态记录。
 * 写入前会通过 adapter 做按 noticeId 去重，副作用是更新 localStorage，供刷新页面后恢复 NEW 与去重状态。
 *
 * @param key localStorage 中的记录键名
 * @param records 需要持久化的通知状态记录
 * @returns void
 */
const saveNoticeRecords = (key, records) => {
    localStorage.setItem(key, stringifyNoticeNewRecords(records));
};

/**
 * 判断实时推送是否应该显示在当前 tab。
 * tab 列表由后端分页加载，socket 增量只插入匹配的当前分类，避免在“通知”tab 下混入“公告”数据。
 *
 * @param message 已适配的通知消息
 * @returns true 表示可以插入当前列表
 */
const shouldRenderInCurrentTab = (message) => {
    if (currentTab.value === 0) return true;
    return message.category === tabs.value[currentTab.value]?.category;
};

/**
 * 处理通知点击。
 * 点击后清除当前消息的 NEW 持久化记录，再打开详情抽屉；已处理推送指纹不清除，用于继续拦截重复推送。
 *
 * @param msg 被点击的通知消息
 * @returns Promise<void>；副作用是更新 NEW 状态、关闭列表面板并展示详情 HTML
 */
const handleMsgClick = async (msg) => {
    // 点击查看只清除 NEW 状态；已处理推送指纹会继续保留，用于拦截重连后的重复补发。
    msg.unread = false;
    msg.isNew = false;
    saveNoticeRecords(NOTIFY_NEW_STORAGE_KEY, removeNoticeNewRecord(getNoticeRecords(NOTIFY_NEW_STORAGE_KEY), msg.id));
    updateTabsCount();
    emits('close');

    currentDrawer.value = { title: msg.title };
    drawerOpen.value = true;
    drawerLoading.value = false;
    drawerContent.value = msg.contentHtml || msg.desc || '';
};

const closeDrawer = () => {
    drawerOpen.value = false;
    drawerContent.value = '';
}

/**
 * 刷新各 tab 未读数量。
 * 数量按 tab 自身 category 统计，避免升级公告和系统通知的计数互相串位。
 *
 * @returns 无返回值；副作用是更新 tabs 中的 count 字段
 */
const updateTabsCount = () => {
    const records = getNoticeRecords(NOTIFY_NEW_STORAGE_KEY);
    tabs.value.forEach((tab) => {
        tab.count = tab.category === 'all'
            ? records.length
            : records.filter((record) => record.category === tab.category).length;
    });
    // 顶部导航只关心全部未读数，内部 tab 计数变化后同步通知父组件控制红点显隐。
    emits('unread-change', tabs.value[0].count);
};

/**
 * 从公告接口分页加载通知并恢复本地 NEW 状态。
 * 接口历史数据默认已读，只有仍存在 NEW 持久化记录的通知才恢复未读角标，避免刷新后丢失 socket 新消息状态。
 *
 * @param reset true 表示重置到第一页，false 表示加载下一页
 * @returns Promise<void>；副作用是更新 messages 列表与未读数量
 */
const loadMessagesFromList = async (reset = true) => {
    if (loadingList.value || loadingMore.value) return;
    if (reset) queryPage.value = 1;

    const isFirstPage = queryPage.value === 1;
    loadingList.value = isFirstPage;
    loadingMore.value = !isFirstPage;
    try {
        // 顶部通知统一从业务公告接口取数，避免继续读取 window.__NOTIFY_MESSAGES__ 造成静态配置和后台公告不一致。
        const res = await listNotice({
            pageNum: queryPage.value,
            pageSize: NOTIFY_PAGE_SIZE,
            noticeTitle: '',
            createByName: '',
            noticeType: currentNoticeType.value,
            status: 0
        });
        // 列表接口只是历史公告快照，默认不触发未读红点；只有 socket 推送才表示新的用户触达。
        const newRecords = getNoticeRecords(NOTIFY_NEW_STORAGE_KEY);
        const nextMessages = applyNoticeNewRecords(
            (res.rows || []).map((notice) => toNotifyMessage(notice, { unread: false })),
            newRecords
        );

        if (reset) {
            messages.value = nextMessages;
        } else {
            const existingIds = new Set(messages.value.map((message) => message.id));
            messages.value = messages.value.concat(nextMessages.filter((message) => !existingIds.has(message.id)));
        }

        total.value = Number(res.total || 0);
        queryPage.value += 1;
        updateTabsCount();
    } finally {
        // 接口异常时也必须释放 loading 状态，否则滚动加载会被永久拦截。
        loadingList.value = false;
        loadingMore.value = false;
    }

};

/**
 * 接收顶部栏广播的实时公告并合并到当前通知列表。
 * socket 只推送单条数据，先按内容指纹拦截重复补发，再将真正的新触达写入 NEW 持久化记录。
 *
 * @param payload /global/notice 推送的原始载荷
 * @returns 无返回值；副作用是更新通知列表和未读数量
 */
const handleRealtimeNotice = (payload) => {
    const notice = normalizeNoticePayload(payload);
    if (!notice) return;
    if (!isActiveNotice(notice)) {
        // status=1 表示该公告不再作为有效提醒展示；收到停用推送时要同步清掉本地 NEW，避免旧角标残留。
        saveNoticeRecords(NOTIFY_NEW_STORAGE_KEY, removeNoticeNewRecord(getNoticeRecords(NOTIFY_NEW_STORAGE_KEY), notice.noticeId));
        messages.value = messages.value.map((message) => (
            message.id === String(notice.noticeId)
                ? { ...message, unread: false, isNew: false }
                : message
        ));
        updateTabsCount();
        return;
    }
    const pushedRecords = getNoticeRecords(NOTIFY_PUSH_RECORD_STORAGE_KEY);
    if (isDuplicateNoticePush(pushedRecords, notice)) return;

    // socket 推送代表新的用户触达事件，只有它会写入 NEW；接口初始化数据仍保持默认已读。
    const incoming = toNotifyMessage(notice, { unread: true, isNew: true });
    const incomingRecord = createNoticeNewRecord(incoming);
    saveNoticeRecords(NOTIFY_PUSH_RECORD_STORAGE_KEY, [incomingRecord, ...pushedRecords]);
    saveNoticeRecords(NOTIFY_NEW_STORAGE_KEY, [incomingRecord, ...getNoticeRecords(NOTIFY_NEW_STORAGE_KEY)]);

    if (shouldRenderInCurrentTab(incoming)) {
        const existed = messages.value.some((message) => message.id === incoming.id);
        messages.value = mergeNoticeMessage(messages.value, incoming);
        if (!existed) total.value += 1;
    }
    updateTabsCount();
};

/**
 * 切换通知分类并重新拉取第一页。
 * 当前分页由后端按 noticeType 筛选，切换 tab 时必须重置列表和页码，否则会把不同分类的分页结果拼在一起。
 *
 * @param index 被点击的 tab 下标
 * @returns void
 */
const handleTabClick = (index) => {
    if (currentTab.value === index && messages.value.length) return;
    currentTab.value = index;
    messages.value = [];
    total.value = 0;
    loadMessagesFromList(true);
};

/**
 * 处理通知列表滚动分页。
 * 通过短防抖合并连续 scroll 事件，只有接近底部且后端仍有剩余数据时才请求下一页；定时器会在组件卸载时清理。
 *
 * @param event 滚动事件对象
 * @returns void
 */
const handleMessageScroll = (event) => {
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
        const target = event.target;
        const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 24;
        if (nearBottom && messages.value.length < total.value) {
            loadMessagesFromList(false);
        }
    }, NOTIFY_SCROLL_DEBOUNCE);
};

onMounted(async () => {
    await loadMessagesFromList();
    emitter.on(EVENT_GLOBAL_NOTICE_RECEIVED, handleRealtimeNotice);
});

watch(() => props.show, (val) => {
    if (val) {
        setTimeout(() => {
            document.addEventListener('click', closePanelGlobally);
        }, 200);
    } else {
        document.removeEventListener('click', closePanelGlobally);
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('click', closePanelGlobally);
    emitter.off(EVENT_GLOBAL_NOTICE_RECEIVED, handleRealtimeNotice);
    if (scrollTimer) clearTimeout(scrollTimer);
});
</script>

<style lang="scss" scoped>
/* ========== 消息面板 ========== */
.notification-wrapper {
    position: relative;
    display: inline-block;

    .notif-panel {
        position: absolute;
        top: 0;
        right: 0;
        width: 380px;
        background: #0f1114;
        border: 1px solid #343539;
        border-radius: 8px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
        z-index: 200;
        overflow: hidden;
        opacity: 0;
        transform: translateY(-6px) scale(.98);
        pointer-events: none;
        transition: all .2s ease;
        font-family: HarmonyOS Sans SC;

        &.open {
            opacity: 1;
            transform: translateY(0) scale(1);
            pointer-events: all;
        }

        .panel-topline {
            height: 2px;
            background: linear-gradient(90deg, transparent, #0d83fc, #0d83fc, transparent);
        }

        .panel-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px 8px;

            .panel-title {
                font-size: 14px;
                font-weight: 600;
                color: #c1c3c4;
                display: flex;
                align-items: center;
                gap: 6px;

                &::before {
                    content: '';
                    display: inline-block;
                    width: 3px;
                    height: 14px;
                    background: #0d83fc;
                    border-radius: 2px;
                }
            }

            .mark-all-btn {
                font-size: 12px;
                color: #929bb2;
                background: transparent;
                border: 1px solid #343539;
                border-radius: 4px;
                padding: 4px 10px;
                cursor: pointer;
                transition: all .15s;

                &:hover {
                    color: #0d83fc;
                    border-color: #0d83fc;
                }
            }
        }

        .panel-tabs {
            display: flex;
            padding: 0 16px;
            border-bottom: 1px solid #343539;
            gap: 4px;

            .tab-btn {
                font-size: 13px;
                color: #929bb2;
                background: none;
                border: none;
                cursor: pointer;
                padding: 8px 12px;
                border-bottom: 2px solid transparent;
                margin-bottom: -1px;
                transition: all .15s;
                display: flex;
                align-items: center;
                gap: 4px;

                &.active {
                    color: #0d83fc;
                    border-bottom-color: #0d83fc;
                }

                .tab-count {
                    background: #ef4444;
                    color: #fff;
                    border-radius: 9px;
                    font-size: 10px;
                    font-weight: 600;
                    min-width: 16px;
                    height: 16px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 4px;
                }
            }
        }

        .msg-list {
            max-height: 320px;
            overflow-y: auto;
            padding: 4px 0;

            &::-webkit-scrollbar {
                width: 4px;
            }

            &::-webkit-scrollbar-track {
                background: transparent;
            }

            &::-webkit-scrollbar-thumb {
                background: #343539;
                border-radius: 2px;
            }

            .msg-empty,
            .msg-loading-more {
                padding: 28px 16px;
                color: #64748b;
                font-size: 12px;
                text-align: center;
            }

            .msg-loading-more {
                padding: 10px 16px;
            }

            .msg-card {
                display: flex;
                gap: 10px;
                padding: 10px 16px;
                cursor: pointer;
                position: relative;
                border-left: 2px solid transparent;
                transition: all .15s;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 16px;
                    right: 16px;
                    height: 1px;
                    background: rgba(78, 79, 81, 0.3);
                }

                &:last-child::after {
                    display: none;
                }

                &:hover {
                    background: #1e2125;
                }

                &.unread {
                    background: rgba(13, 131, 252, 0.06);
                    border-left-color: #0d83fc;

                    &:hover {
                        background: rgba(13, 131, 252, 0.1);
                    }
                }

                &.warn {
                    border-left-color: #f59e0b;

                    &.unread {
                        background: rgba(245, 158, 11, 0.06);
                    }
                }

                &.success {
                    border-left-color: #22c55e;
                }

                .msg-icon {
                    width: 34px;
                    height: 34px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                    flex-shrink: 0;

                    &.blue {
                        background: rgba(13, 131, 252, 0.12);
                        border: 1px solid rgba(13, 131, 252, 0.2);
                    }

                    &.orange {
                        background: rgba(245, 158, 11, 0.12);
                        border: 1px solid rgba(245, 158, 11, 0.2);
                    }

                    &.green {
                        background: rgba(34, 197, 94, 0.12);
                        border: 1px solid rgba(34, 197, 94, 0.2);
                    }
                }

                .msg-body {
                    flex: 1;
                    min-width: 0;

                    .msg-title {
                        font-size: 13px;
                        font-weight: 500;
                        color: #c1c3c4;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        margin-bottom: 3px;
                        min-width: 0;

                        .unread-dot {
                            width: 6px;
                            height: 6px;
                            background: #0d83fc;
                            border-radius: 50%;
                            flex-shrink: 0;
                        }

                        .msg-title-text {
                            flex: 1;
                            min-width: 0;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        .msg-new-badge {
                            font-size: 10px;
                            line-height: 14px;
                            padding: 0 5px;
                            border-radius: 7px;
                            color: #fff;
                            background: #ef4444;
                            font-weight: 700;
                            flex-shrink: 0;
                        }

                        .msg-tag {
                            font-size: 10px;
                            padding: 1px 6px;
                            border-radius: 2px;
                            font-weight: 500;
                            white-space: nowrap;
                            flex-shrink: 0;

                            &.new {
                                background: rgba(13, 131, 252, 0.15);
                                color: #0d83fc;
                                border: 1px solid rgba(13, 131, 252, 0.25);
                            }

                            &.fixed {
                                background: rgba(34, 197, 94, 0.12);
                                color: #22c55e;
                                border: 1px solid rgba(34, 197, 94, 0.2);
                            }

                            &.notice {
                                background: rgba(13, 131, 252, 0.15);
                                color: #0d83fc;
                                border: 1px solid rgba(13, 131, 252, 0.25);
                            }

                            &.announcement {
                                background: rgba(245, 158, 11, 0.12);
                                color: #f59e0b;
                                border: 1px solid rgba(245, 158, 11, 0.2);
                            }

                            &.update {
                                background: rgba(34, 197, 94, 0.12);
                                color: #22c55e;
                                border: 1px solid rgba(34, 197, 94, 0.2);
                            }
                        }
                    }

                    .msg-desc {
                        font-size: 12px;
                        color: #929bb2;
                        line-height: 1.5;
                        margin-bottom: 3px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .msg-time {
                        font-size: 11px;
                        color: #64748b;
                    }
                }
            }
        }
    }
}
</style>

<style lang="scss">
/* ========== 抽屉详情 (全局样式, Teleport 到 body) ========== */
.drawer-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    z-index: 300;
    opacity: 0;
    pointer-events: none;
    transition: opacity .2s;

    &.open {
        opacity: 1;
        pointer-events: all;
    }
}

.drawer {
    position: fixed;
    top: 0;
    right: 0;
    width: min(820px, 88vw);
    height: 100vh;
    background: #0f1114;
    box-shadow: -4px 0 40px rgba(0, 0, 0, 0.6);
    z-index: 301;
    transform: translateX(100%);
    transition: transform .25s ease;
    display: flex;
    flex-direction: column;
    font-family: HarmonyOS Sans SC;

    @media (max-width: 640px) {
        width: 100vw;
    }

    &.open {
        transform: translateX(0);
    }

    .drawer-topline {
        height: 2px;
        background: linear-gradient(90deg, transparent, #0d83fc, #0d83fc, transparent);
    }

    .drawer-header {
        padding: 16px 20px;
        border-bottom: 1px solid #343539;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .drawer-title {
            font-size: 15px;
            font-weight: 600;
            color: #c1c3c4;
            display: flex;
            align-items: center;
            gap: 8px;

            &::before {
                content: '';
                display: inline-block;
                width: 3px;
                height: 14px;
                background: #0d83fc;
                border-radius: 2px;
            }
        }

        .drawer-close {
            width: 28px;
            height: 28px;
            background: transparent;
            border: 1px solid #343539;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            color: #929bb2;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all .15s;

            &:hover {
                color: #c1c3c4;
                border-color: #64748b;
            }
        }
    }

    .drawer-body {
        flex: 1;
        overflow-y: auto;
        padding: 20px;

        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: #343539;
            border-radius: 2px;
        }

        /* === 加载状态 === */
        .drawer-loading {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 60px 0;
            color: #64748b;
            font-size: 13px;
        }

        .loading-spinner {
            width: 18px;
            height: 18px;
            border: 2px solid rgba(13, 131, 252, 0.15);
            border-top-color: #0d83fc;
            border-radius: 50%;
            animation: spin .6s linear infinite;
        }

        @keyframes spin {
            to {
                transform: rotate(360deg);
            }
        }

        /* === Markdown 全局默认 === */
        font-size: 13px;
        line-height: 1.75;
        color: #c1c3c4;

        /* === 段落 === */
        p {
            color: #c1c3c4;
            margin: 0 0 14px;
            line-height: 1.75;
            font-size: 13px;
        }

        /* === 各级标题 === */
        h1 {
            font-size: 20px;
            font-weight: 700;
            color: #e2e4e6;
            margin: 24px 0 14px;
            padding-bottom: 10px;
            border-bottom: 1px solid #343539;
            display: flex;
            align-items: center;
            gap: 8px;

            &::before {
                content: '';
                display: inline-block;
                width: 4px;
                height: 18px;
                background: #0d83fc;
                border-radius: 2px;
            }
        }

        h2 {
            font-size: 16px;
            font-weight: 600;
            color: #e2e4e6;
            margin: 20px 0 12px;
            display: flex;
            align-items: center;
            gap: 6px;

            &::before {
                content: '';
                display: inline-block;
                width: 3px;
                height: 14px;
                background: #0d83fc;
                border-radius: 2px;
            }
        }

        h3 {
            font-size: 13px;
            font-weight: 600;
            color: #c1c3c4;
            margin: 16px 0 10px;
            display: flex;
            align-items: center;
            gap: 6px;

            &::before {
                content: '';
                display: inline-block;
                width: 3px;
                height: 12px;
                background: #0d83fc;
                border-radius: 2px;
            }
        }

        h4 {
            font-size: 13px;
            font-weight: 600;
            color: #c1c3c4;
            margin: 14px 0 8px;
        }

        h5,
        h6 {
            font-size: 12px;
            font-weight: 500;
            color: #929bb2;
            margin: 12px 0 6px;
        }

        /* === 行内强调 === */
        strong,
        b {
            color: #e2e4e6;
            font-weight: 600;
        }

        em,
        i {
            color: #c1c3c4;
            font-style: italic;
        }

        /* === 链接 === */
        a {
            color: #0d83fc;
            text-decoration: none;
            border-bottom: 1px solid rgba(13, 131, 252, 0.3);
            transition: all .15s;

            &:hover {
                color: #60a5fa;
                border-bottom-color: #60a5fa;
            }
        }

        /* === 表格 === */
        table {
            width: 100%;
            table-layout: auto;
            border-collapse: collapse;
            margin: 16px 0;
            font-size: 12px;
            border: 1px solid #343539;
            border-radius: 6px;
            overflow: hidden;
        }

        thead {
            background: rgba(13, 131, 252, 0.08);

            th {
                color: #c1c3c4;
                font-weight: 600;
                font-size: 12px;
                padding: 10px 14px;
                text-align: left;
                border-bottom: 2px solid #343539;
                border-right: 1px solid rgba(52, 53, 57, 0.5);
                white-space: normal;
                word-break: break-word;

                &:first-child {
                    white-space: nowrap;
                    word-break: normal;
                    min-width: 85px;
                    width: 1%;
                }

                &:last-child {
                    border-right: none;
                }
            }
        }

        tbody {
            tr {
                transition: background .12s;

                &:nth-child(even) {
                    background: rgba(255, 255, 255, 0.02);
                }

                &:hover {
                    background: rgba(13, 131, 252, 0.04);
                }
            }

            td {
                color: #929bb2;
                padding: 9px 14px;
                border-bottom: 1px solid rgba(52, 53, 57, 0.4);
                border-right: 1px solid rgba(52, 53, 57, 0.3);
                line-height: 1.6;
                white-space: normal;
                word-break: break-word;

                &:first-child {
                    white-space: nowrap;
                    word-break: normal;
                    min-width: 85px;
                    width: 1%;
                }

                &:last-child {
                    border-right: none;
                }
            }

            tr:last-child td {
                border-bottom: none;
            }
        }

        /* === 列表 === */
        ul {
            list-style: none;
            margin-bottom: 18px;
            padding: 0;

            li {
                font-size: 13px;
                color: #929bb2;
                padding: 7px 0;
                border-bottom: 1px solid rgba(78, 79, 81, 0.3);
                display: flex;
                align-items: flex-start;
                gap: 8px;
                line-height: 1.6;

                &::before {
                    content: '';
                    display: block;
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    flex-shrink: 0;
                    margin-top: 6px;
                    background: #0d83fc;
                }

                &:last-child {
                    border-bottom: none;
                }
            }
        }

        ol {
            padding-left: 20px;
            margin-bottom: 18px;

            li {
                font-size: 13px;
                color: #929bb2;
                padding: 7px 0;
                border-bottom: 1px solid rgba(78, 79, 81, 0.3);
                line-height: 1.6;

                &:last-child {
                    border-bottom: none;
                }

                &::marker {
                    color: #0d83fc;
                    font-weight: 600;
                }
            }
        }

        /* === 嵌套列表不重复底部 border === */
        ul ul,
        ul ol,
        ol ul,
        ol ol {
            margin-top: 6px;
            margin-bottom: 0;

            li {
                border-bottom: none;
                padding: 4px 0;
            }
        }

        /* === 水平线 === */
        hr {
            border: none;
            height: 1px;
            background: linear-gradient(90deg, transparent, #343539, transparent);
            margin: 24px 0;
        }

        /* === 行内代码 === */
        code:not(pre code) {
            background: rgba(13, 131, 252, 0.08);
            color: #60a5fa;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 12px;
            font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', Consolas, monospace;
            border: 1px solid rgba(13, 131, 252, 0.15);
        }

        /* === 代码块 === */
        pre {
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid #343539;
            border-radius: 6px;
            padding: 14px 16px;
            overflow-x: auto;
            margin: 16px 0;
            line-height: 1.6;

            code {
                color: #c1c3c4;
                font-size: 12px;
                background: transparent;
                border: none;
                padding: 0;
                font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', Consolas, monospace;
            }
        }

        /* === 引用块 === */
        blockquote {
            border-left: 3px solid #0d83fc;
            margin: 16px 0;
            padding: 10px 16px;
            background: rgba(13, 131, 252, 0.04);
            border-radius: 0 6px 6px 0;
            color: #929bb2;

            p {
                margin: 0;
                color: #929bb2;
            }

            blockquote {
                margin-top: 8px;
                border-left-color: #f59e0b;
                background: rgba(245, 158, 11, 0.04);
            }
        }

        /* === 图片 === */
        img {
            max-width: 100%;
            border-radius: 6px;
            margin: 12px 0;
        }

        /* === 标记/高亮 === */
        mark {
            background: rgba(245, 158, 11, 0.2);
            color: #fbbf24;
            padding: 1px 4px;
            border-radius: 2px;
        }

        /* === 删除线 === */
        del {
            color: #64748b;
            text-decoration: line-through;
        }

        /* === 打印 / 页内跳转兼容 === */
        @media print {
            background: #fff;
            color: #1e293b;
        }

        /* === 保留旧版自定义 HTML 模块兼容 === */
        .ver-header {
            display: flex;
            gap: 14px;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid #343539;
        }

        .ver-icon {
            width: 52px;
            height: 52px;
            background: rgba(13, 131, 252, 0.12);
            border: 1px solid rgba(13, 131, 252, 0.2);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
            flex-shrink: 0;
        }

        .ver-info {
            flex: 1;
        }

        .ver-num {
            font-size: 20px;
            font-weight: 700;
            color: #c1c3c4;
            margin-bottom: 6px;
        }

        .ver-meta {
            display: flex;
            gap: 8px;
            align-items: center;
            flex-wrap: wrap;
        }

        .vtag {
            font-size: 10px;
            padding: 2px 7px;
            border-radius: 3px;
            font-weight: 500;
        }

        .vtag.latest {
            background: rgba(13, 131, 252, 0.15);
            color: #0d83fc;
            border: 1px solid rgba(13, 131, 252, 0.25);
        }

        .vtag.date {
            background: rgba(255, 255, 255, 0.05);
            color: #929bb2;
            border: 1px solid #343539;
        }

        .perf-block {
            margin-bottom: 18px;
        }

        .perf-item {
            margin-bottom: 10px;
        }

        .perf-label {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #929bb2;
            margin-bottom: 4px;
        }

        .perf-bar {
            height: 4px;
            background: rgba(78, 79, 81, 0.3);
            border-radius: 2px;
            overflow: hidden;
        }

        .perf-fill {
            height: 100%;
            border-radius: 2px;
            transition: width 1.2s ease;
            position: relative;
        }

        .perf-fill::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 6px;
            height: 100%;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
        }

        .perf-fill.blue {
            background: linear-gradient(90deg, #0d83fc, #60a5fa);
        }

        .perf-fill.green {
            background: linear-gradient(90deg, #16a34a, #22c55e);
        }

        .perf-fill.cyan {
            background: linear-gradient(90deg, #0891b2, #22d3ee);
        }
    }
}
</style>
